import { createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { DeployStatus } from "@asichain/asi-wallet-sdk";
import {
    WalletStoreState,
    IWalletMeta,
    IAccountMeta,
    IDeployWatchState,
    IUnlockedWalletMeta,
    Network,
    TCustomNetwork,
} from "types/wallet";
import { RootState } from "store";
import { getInitialNetwork, NETWORKS } from "constants/networks";
import {
    importKeyfileAccounts,
    addCustomNetwork,
    deployContract,
    IInitializeNetworksResponse,
    initializeNetworks,
    IRemoveNetworkResponse,
    loadWalletsFromStorage,
    removeAccount,
    removeCustomNetwork,
    removeWallet,
    selectAccount,
    selectNetwork,
    sendTransaction,
    updateAccountName,
    updateCustomNetwork,
} from "./thunks";
import {
    addWalletToWalletsStore,
    applyActiveWalletSession,
    getUnlockedAccountFromWalletsMeta,
    toLockedWalletMeta,
} from "./helpers";
import { walletsApi } from "./api";
import {
    createHdWallet,
    deriveHdAccount,
    importHdWallet,
    importKeyfileWallet,
    importPrivateKeyWallet,
    loginWithPassword,
    logout,
} from "store/Auth/thunks";

const initialState: WalletStoreState = {
    wallets: [],
    selectedAccountId: null,
    networks: [...NETWORKS],
    selectedNetwork: getInitialNetwork(),
    deployWatches: {},
    isLoading: false,
    isInitialLoadComplete: false,
};

export interface IAccountDefaultUpdateFieldsPayload {
    walletId: string;
    accountId: string;
}

export interface IDeployStatusChangedPayload {
    deployId: string;
    status: DeployStatus;
}

export interface IDeployWatchUnresolvedPayload {
    deployId: string;
    reason: string;
}

const walletsStoreSlice = createSlice({
    name: "wallets-store",
    initialState,
    reducers: {
        deployStatusChanged: (
            state,
            action: PayloadAction<IDeployStatusChangedPayload>,
        ) => {
            const { deployId, status } = action.payload;

            state.deployWatches[deployId] = { status };
        },
        deployWatchUnresolved: (
            state,
            action: PayloadAction<IDeployWatchUnresolvedPayload>,
        ) => {
            const { deployId, reason } = action.payload;
            const watch = state.deployWatches[deployId];

            if (!watch) {
                return;
            }

            watch.unresolvedReason = reason;
        },
        deployWatchCleared: (state, action: PayloadAction<string>) => {
            delete state.deployWatches[action.payload];
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(loadWalletsFromStorage.fulfilled, (state, action) => {
                state.wallets = action.payload;
                state.isInitialLoadComplete = true;
            })
            .addCase(loadWalletsFromStorage.rejected, (state) => {
                state.isInitialLoadComplete = true;
            })
            .addCase(importKeyfileAccounts.fulfilled, (state, action) => {
                addWalletToWalletsStore(state.wallets, action.payload);
            })
            .addCase(selectAccount.fulfilled, (state, action) => {
                state.selectedAccountId = action.payload;
            })
            .addCase(removeWallet.fulfilled, (state, action) => {
                const { removedWalletId, removedSignerId } = action.payload;

                state.wallets = state.wallets.filter((walletMeta) => {
                    if (walletMeta.isUnlocked) {
                        return walletMeta.id !== removedWalletId;
                    }

                    return walletMeta.signerId !== removedSignerId;
                });

                if (!state.wallets.length) {
                    state.selectedAccountId = null;

                    return;
                }

                state.selectedAccountId = state.wallets[0].accounts[0].id;
            })
            .addCase(removeAccount.fulfilled, (state, action) => {
                const { walletId, accountId, selectedAccountId } =
                    action.payload;

                const wallet = state.wallets.find(
                    (walletMeta) => walletMeta.id === walletId,
                );

                if (!wallet) {
                    throw new Error(
                        "walletsStoreSlice.removeAccount: Incorrect wallet id",
                    );
                }

                wallet.accounts = wallet.accounts.filter(
                    (accountMeta) => accountMeta.id !== accountId,
                );

                state.selectedAccountId = selectedAccountId;
            })
            .addCase(updateAccountName.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(updateAccountName.fulfilled, (state, action) => {
                const { accountId, name } = action.payload;

                const targetAccount: IAccountMeta | null =
                    getUnlockedAccountFromWalletsMeta(state.wallets, accountId);

                if (!targetAccount) {
                    console.error(
                        "walletsStoreSlice.updateAccountName: Incorrect account id",
                    );

                    return;
                }

                targetAccount.name = name;
                state.isLoading = false;
            })
            .addCase(updateAccountName.rejected, (state) => {
                state.isLoading = false;
            })
            .addCase(
                initializeNetworks.fulfilled,
                (state, action: PayloadAction<IInitializeNetworksResponse>) => {
                    const { customNetworks, selectedNetwork } = action.payload;

                    state.networks = [
                        ...state.networks.filter(
                            (network: Network) => network.isDefault,
                        ),
                        ...customNetworks,
                    ];

                    if (selectedNetwork) {
                        state.selectedNetwork = selectedNetwork;
                    }
                },
            )
            .addCase(
                selectNetwork.fulfilled,
                (state, action: PayloadAction<Network>) => {
                    state.selectedNetwork = action.payload;
                },
            )
            .addCase(
                addCustomNetwork.fulfilled,
                (state, action: PayloadAction<TCustomNetwork>) => {
                    state.networks.push(action.payload);
                },
            )
            .addCase(
                updateCustomNetwork.fulfilled,
                (state, action: PayloadAction<TCustomNetwork>) => {
                    const updatedNetwork = action.payload;

                    const targetNetworkIndex = state.networks.findIndex(
                        (network: Network) => network.id === updatedNetwork.id,
                    );

                    if (targetNetworkIndex === -1) {
                        console.error(
                            "walletsStoreSlice.updateCustomNetwork: Incorrect network id",
                        );

                        return;
                    }

                    state.networks[targetNetworkIndex] = updatedNetwork;

                    if (state.selectedNetwork.id === updatedNetwork.id) {
                        state.selectedNetwork = updatedNetwork;
                    }
                },
            )
            .addCase(
                removeCustomNetwork.fulfilled,
                (state, action: PayloadAction<IRemoveNetworkResponse>) => {
                    const { id, selectedNetworkId } = action.payload;

                    state.networks = state.networks.filter(
                        (network: Network) => network.id !== id,
                    );

                    if (state.selectedNetwork.id !== id) {
                        return;
                    }

                    const currentNetwork = state.networks.find(
                        (network: Network) => network.id === selectedNetworkId,
                    );

                    if (!currentNetwork) {
                        console.error(
                            "walletsStoreSlice.removeCustomNetwork: SDK switched to an unknown network",
                        );

                        return;
                    }

                    state.selectedNetwork = currentNetwork;
                },
            )
            .addCase(sendTransaction.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(sendTransaction.fulfilled, (state, action) => {
                state.isLoading = false;
                state.deployWatches[action.payload.deployId] = {
                    status: DeployStatus.DEPLOYING,
                };
            })
            .addCase(sendTransaction.rejected, (state) => {
                state.isLoading = false;
            })
            .addCase(deployContract.fulfilled, (state, action) => {
                state.deployWatches[action.payload.deployId] = {
                    status: DeployStatus.DEPLOYING,
                };
            })
            .addCase(createHdWallet.fulfilled, (state, action) => {
                applyActiveWalletSession(state, action.payload);
            })
            .addCase(importHdWallet.fulfilled, (state, action) => {
                applyActiveWalletSession(state, action.payload);
            })
            .addCase(importPrivateKeyWallet.fulfilled, (state, action) => {
                applyActiveWalletSession(state, action.payload);
            })
            .addCase(importKeyfileWallet.fulfilled, (state, action) => {
                applyActiveWalletSession(state, action.payload);
            })
            .addCase(deriveHdAccount.fulfilled, (state, action) => {
                applyActiveWalletSession(state, action.payload);
            })
            .addCase(loginWithPassword.fulfilled, (state, action) => {
                applyActiveWalletSession(state, action.payload);
            })
            .addCase(logout.fulfilled, (state) => {
                state.wallets = state.wallets.map(toLockedWalletMeta);
                state.selectedAccountId = null;
            });
    },
});

export const selectAccountById = (state: RootState, accountId: string) =>
    getUnlockedAccountFromWalletsMeta(state.walletsStore.wallets, accountId);
export const selectWalletByAccountId = (state: RootState, accountId: string) =>
    state.walletsStore.wallets.find((walletMeta: IWalletMeta) =>
        walletMeta.accounts.some(
            (accountMeta: IAccountMeta) => accountMeta.id === accountId,
        ),
    ) ?? null;
export const selectWalletByFilter = (
    state: RootState,
    filter: (
        walletMeta: IWalletMeta,
        index: number,
        array: IWalletMeta[],
    ) => boolean,
) => state.walletsStore.wallets.find(filter) ?? null;
export const selectWallets = (state: RootState) => state.walletsStore.wallets;
export const selectActiveWallet = createSelector(
    [selectWallets, (state: RootState) => state.auth.activeSignerId],
    (
        wallets: IWalletMeta[],
        activeSignerId: string | null,
    ): IUnlockedWalletMeta | null =>
        wallets.find(
            (walletMeta: IWalletMeta): walletMeta is IUnlockedWalletMeta =>
                walletMeta.isUnlocked && walletMeta.signerId === activeSignerId,
        ) ?? null,
);

export const selectAccounts = createSelector(
    [selectActiveWallet],
    (activeWallet: IUnlockedWalletMeta | null) => activeWallet?.accounts ?? [],
);
export const selectSelectedAccountId = (state: RootState) =>
    state.walletsStore.selectedAccountId;
export const selectSelectedAccount = (state: RootState) => {
    return state.walletsStore.selectedAccountId
        ? selectAccountById(state, state.walletsStore.selectedAccountId)
        : null;
};

export const selectNetworks = (state: RootState) => state.walletsStore.networks;
export const selectSelectedNetwork = (state: RootState) =>
    state.walletsStore.selectedNetwork;
export const selectSelectedNetworkId = (state: RootState) =>
    state.walletsStore.selectedNetwork.id;

export const selectCustomNetworks = createSelector(
    [selectNetworks],
    (networks: Network[]): Network[] =>
        networks.filter((network: Network) => !network.isDefault),
);

export const selectIsAnyAccountBalanceFetching = (
    state: RootState,
): boolean => {
    const networkId = selectSelectedNetworkId(state);

    return selectAccounts(state).some(
        (accountMeta: IAccountMeta) =>
            walletsApi.endpoints.getBalance.select({
                accountId: accountMeta.id,
                networkId,
            })(state).isLoading,
    );
};

export const selectUnlockedWallets = createSelector(
    [selectWallets],
    (wallets: IWalletMeta[]): IUnlockedWalletMeta[] =>
        wallets.filter(
            (walletMeta: IWalletMeta): walletMeta is IUnlockedWalletMeta =>
                walletMeta.isUnlocked,
        ),
);
export const selectHasWallets = (state: RootState) =>
    state.walletsStore.wallets.length > 0;
export const selectWalletsInitialLoadComplete = (state: RootState) =>
    state.walletsStore.isInitialLoadComplete;
export const selectIsAccountUnlocked = (state: RootState, accountId: string) =>
    state.walletsStore.wallets.some(
        (w) => w.isUnlocked && w.accounts.some((a) => a.id === accountId),
    );
export const selectDeployWatch = (
    state: RootState,
    deployId: string,
): IDeployWatchState | null =>
    state.walletsStore.deployWatches[deployId] ?? null;

export const {
    deployStatusChanged,
    deployWatchUnresolved,
    deployWatchCleared,
} = walletsStoreSlice.actions;

export default walletsStoreSlice.reducer;
